import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Bell, RefreshCw, Send, AlertTriangle, Shield } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";

const EVENT_TYPES = [
  "partner_booking_attributed",
  "kit_sent",
  "sample_received",
  "results_published",
  "summary_report_available",
  "commission_ledger_created",
  "commission_status_changed",
];

const EVENT_TONE = {
  partner_booking_attributed: { bg: "bg-cyan-100", fg: "text-cyan-900", short: "Booking" },
  kit_sent:                   { bg: "bg-amber-100", fg: "text-amber-900", short: "Kit" },
  sample_received:            { bg: "bg-violet-100", fg: "text-violet-900", short: "Sample" },
  results_published:          { bg: "bg-emerald-100", fg: "text-emerald-900", short: "Results" },
  summary_report_available:   { bg: "bg-sky-100", fg: "text-sky-900", short: "Summary" },
  commission_ledger_created:  { bg: "bg-lime-100", fg: "text-lime-900", short: "Ledger" },
  commission_status_changed:  { bg: "bg-rose-100", fg: "text-rose-900", short: "Status" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", {dateStyle: "short", timeStyle: "short"}); }
  catch { return iso; }
}

export default function AdminPWSNotifications() {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState(null);
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [bookingFilter, setBookingFilter] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [busy, setBusy] = useState(null);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (eventTypeFilter) params.event_type = eventTypeFilter;
      if (bookingFilter.trim()) params.booking_reference = bookingFilter.trim();
      if (partnerFilter.trim()) params.partner_id = partnerFilter.trim();
      const [{ data: list }, { data: sum }, { data: s }] = await Promise.all([
        api.get("/admin/pws-notifications", { params }),
        api.get("/admin/pws-notifications/summary"),
        api.get("/admin/pws-notifications/settings"),
      ]);
      setEvents(list.events || []);
      setCounts(sum.counts || null);
      setSettings(s.settings || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = { limit: 100 };
        if (eventTypeFilter) params.event_type = eventTypeFilter;
        const [{ data: list }, { data: sum }, { data: s }] = await Promise.all([
          api.get("/admin/pws-notifications", { params }),
          api.get("/admin/pws-notifications/summary"),
          api.get("/admin/pws-notifications/settings"),
        ]);
        if (cancelled) return;
        setEvents(list.events || []);
        setCounts(sum.counts || null);
        setSettings(s.settings || []);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Failed to load notifications");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventTypeFilter]);

  const toggleSetting = async (event_type, field, value) => {
    setBusy(`${event_type}::${field}`);
    try {
      const { data } = await api.patch(
        `/admin/pws-notifications/settings/${event_type}`,
        { [field]: value },
      );
      setSettings((cur) => cur.map(s => s.event_type === event_type ? data.settings : s));
      toast.success(`Updated ${event_type}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  const dispatch = async (event_id) => {
    setBusy(event_id);
    try {
      const { data } = await api.post(`/admin/pws-notifications/${event_id}/dispatch`);
      if (data.dispatched) toast.success("Dispatched");
      else toast.message(data.reason || "Skipped (safe mode)");
      await loadEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch failed");
    } finally {
      setBusy(null);
    }
  };

  const totalsRow = useMemo(() => counts || {}, [counts]);

  return (
    <PortalShell>
      <div data-testid="admin-pws-notifications-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/admin/partners/network"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 hover:text-cyan-800"
          data-testid="back-to-pws-network-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Network
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">
              Aware Partner Network · Phase 11
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              Partner Notification Events
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-3xl">
              Live event log for every partner-relevant booking + commission lifecycle event.
              <strong> Partner alert emails send automatically</strong> for any event type whose
              partner send-switch is ON (and the global dispatch flag is enabled in the
              environment). Booking-attributed and results-published alerts are on by default.
            </p>
          </div>
          <button
            type="button"
            onClick={loadEvents}
            disabled={loading}
            data-testid="admin-pws-notifications-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-60"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Safety banner — always shown so the team can't miss it */}
        <div
          data-testid="admin-pws-notifications-safety-banner"
          className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3"
        >
          <Shield className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-900">
            <strong>Safety boundary (pinned):</strong> Partner alert emails dispatch
            automatically when an event is recorded — but ONLY when that event&apos;s
            partner send-switch below is ON AND the global env flag
            <code className="mx-1 px-1 rounded bg-amber-100 text-amber-800 font-mono">
              PWS_NOTIFICATIONS_DISPATCH_ENABLED
            </code>
            is truthy. Either gate off = nothing sends. Click <em>Dispatch</em> on a row
            to send (or re-attempt) the partner alert email for that event.
          </div>
        </div>

        {/* Event-type chips */}
        <div
          className="mt-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3"
          data-testid="admin-pws-notifications-summary"
        >
          {EVENT_TYPES.map(et => {
            const tone = EVENT_TONE[et];
            const c = (totalsRow && totalsRow[et]) || 0;
            return (
              <button
                key={et}
                type="button"
                data-testid={`admin-pws-notifications-summary-${et}`}
                onClick={() => setEventTypeFilter(eventTypeFilter === et ? "" : et)}
                className={`text-left rounded-xl p-3 ${tone.bg} ${tone.fg} ${eventTypeFilter === et ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
              >
                <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{tone.short}</div>
                <div className="mt-1 text-lg font-bold">{c}</div>
                <div className="text-[11px] opacity-75 truncate" title={et}>{et.replaceAll("_", " ")}</div>
              </button>
            );
          })}
        </div>

        {/* Settings panel */}
        <div className="mt-8" data-testid="admin-pws-notifications-settings-panel">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Send-switch settings (default <strong>OFF</strong>)
          </h2>
          <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left p-3">Event</th>
                  <th className="text-center p-3">Partner email</th>
                  <th className="text-center p-3">Customer email</th>
                  <th className="text-left p-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {settings.map(s => (
                  <tr
                    key={s.event_type}
                    data-testid={`admin-pws-notifications-setting-row-${s.event_type}`}
                    className="border-t border-slate-100"
                  >
                    <td className="p-3">
                      <div className="font-semibold text-slate-800">{s.label}</div>
                      <div className="text-xs text-slate-500 font-mono">{s.event_type}</div>
                    </td>
                    <td className="p-3 text-center">
                      <label className="inline-flex items-center cursor-pointer gap-2">
                        <input
                          type="checkbox"
                          data-testid={`admin-pws-notifications-toggle-partner-${s.event_type}`}
                          checked={!!s.partner_send_enabled}
                          disabled={busy === `${s.event_type}::partner_send_enabled`}
                          onChange={(e) => toggleSetting(s.event_type, "partner_send_enabled", e.target.checked)}
                        />
                        <span className={s.partner_send_enabled ? "text-emerald-700 font-semibold" : "text-slate-500"}>
                          {s.partner_send_enabled ? "ON" : "OFF"}
                        </span>
                      </label>
                    </td>
                    <td className="p-3 text-center">
                      <label className="inline-flex items-center cursor-pointer gap-2">
                        <input
                          type="checkbox"
                          data-testid={`admin-pws-notifications-toggle-customer-${s.event_type}`}
                          checked={!!s.customer_send_enabled}
                          disabled={busy === `${s.event_type}::customer_send_enabled`}
                          onChange={(e) => toggleSetting(s.event_type, "customer_send_enabled", e.target.checked)}
                        />
                        <span className={s.customer_send_enabled ? "text-emerald-700 font-semibold" : "text-slate-500"}>
                          {s.customer_send_enabled ? "ON" : "OFF"}
                        </span>
                      </label>
                    </td>
                    <td className="p-3 text-xs text-slate-500">
                      {s.updated_by ? `${s.updated_by} · ` : ""}{fmtDate(s.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-8 flex items-end gap-3 flex-wrap" data-testid="admin-pws-notifications-filters">
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Booking</label>
            <input
              value={bookingFilter}
              onChange={(e) => setBookingFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") loadEvents(); }}
              placeholder="SCWT-XXXXXX"
              data-testid="admin-pws-notifications-booking-filter"
              className="text-sm px-2 py-1.5 rounded-md border border-slate-200 bg-white w-40"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Partner ID</label>
            <input
              value={partnerFilter}
              onChange={(e) => setPartnerFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") loadEvents(); }}
              placeholder="filter by partner_id"
              data-testid="admin-pws-notifications-partner-filter"
              className="text-sm px-2 py-1.5 rounded-md border border-slate-200 bg-white w-56"
            />
          </div>
          <button
            type="button"
            onClick={loadEvents}
            data-testid="admin-pws-notifications-apply-filters-btn"
            className="text-xs font-semibold px-3 py-1.5 rounded-md bg-cyan-600 text-white hover:bg-cyan-700"
          >
            Apply
          </button>
          {eventTypeFilter && (
            <button
              type="button"
              onClick={() => setEventTypeFilter("")}
              data-testid="admin-pws-notifications-clear-event-filter-btn"
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              Clear event-type filter ({eventTypeFilter})
            </button>
          )}
        </div>

        {/* Events table */}
        <div className="mt-4 rounded-xl bg-white border border-slate-200 overflow-hidden">
          {loading ? (
            <div data-testid="admin-pws-notifications-loading" className="p-10 text-center text-slate-500 text-sm">Loading…</div>
          ) : events.length === 0 ? (
            <div data-testid="admin-pws-notifications-empty" className="p-10 text-center">
              <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="text-sm text-slate-600">No notification events yet.</div>
              <div className="text-xs text-slate-400 mt-1">Events appear when a partner-linked booking, kit, result or commission entry is recorded.</div>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-pws-notifications-table">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left p-3">Event</th>
                  <th className="text-left p-3">Booking</th>
                  <th className="text-left p-3">Partner</th>
                  <th className="text-left p-3">Recorded</th>
                  <th className="text-left p-3">Dispatch</th>
                  <th className="text-right p-3 pr-4">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {events.map(ev => {
                  const tone = EVENT_TONE[ev.event_type] || { bg: "bg-slate-100", fg: "text-slate-700" };
                  const dispatchTone = ev.dispatch_status === "would_send"
                    ? "text-amber-700"
                    : "text-slate-500";
                  return (
                    <tr
                      key={ev.event_id}
                      data-testid={`admin-pws-notifications-event-row-${ev.event_id}`}
                      className="border-t border-slate-100 align-top"
                    >
                      <td className="p-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider ${tone.bg} ${tone.fg}`}>
                          {ev.label || ev.event_type}
                        </span>
                      </td>
                      <td className="p-3 font-mono text-xs text-slate-700">{ev.booking_reference || "—"}</td>
                      <td className="p-3 font-mono text-xs text-slate-700">{ev.partner_id || "—"}</td>
                      <td className="p-3 text-xs text-slate-600">{fmtDate(ev.recorded_at)}</td>
                      <td className={`p-3 text-xs ${dispatchTone}`}>
                        <div className="font-semibold">{ev.dispatch_status || "skipped"}</div>
                        <div className="opacity-80 text-[11px]">{ev.dispatch_reason || ""}</div>
                      </td>
                      <td className="p-3 pr-4 text-right">
                        <button
                          type="button"
                          disabled={busy === ev.event_id}
                          onClick={() => dispatch(ev.event_id)}
                          data-testid={`admin-pws-notifications-dispatch-btn-${ev.event_id}`}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-60"
                        >
                          <Send className="w-3 h-3" /> Dispatch
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div
          data-testid="admin-pws-notifications-disclaimer"
          className="mt-6 text-[11px] text-slate-400 leading-relaxed flex items-start gap-2 max-w-3xl"
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            Partner alert dispatch is LIVE (Jun 2026). Events auto-dispatch a partner email
            the moment they are recorded, when the event&apos;s partner send-switch is ON and
            <code className="mx-1 px-1 rounded bg-amber-100 text-amber-800 font-mono">PWS_NOTIFICATIONS_DISPATCH_ENABLED</code>
            is set. The partner email is resolved from notification_email → contact_email →
            email on the partner account. Customer-side sends remain unwired.
          </div>
        </div>
      </div>
    </PortalShell>
  );
}
